/* @jsx jsx */
import { useIsMobileView } from '@front10/utils';
import { NumberFormatOptions } from '@internationalized/number';
import { useButton } from '@react-aria/button';
import { FocusScope, useFocusManager } from '@react-aria/focus';
import { useNumberFormatter } from '@react-aria/i18n';
import {
  AriaPositionProps,
  OverlayContainer,
  useOverlayPosition,
  useOverlayTrigger
} from '@react-aria/overlays';
import { useOverlayTriggerState } from '@react-stately/overlays';
import { jsx } from '@theme-ui/core';
import * as React from 'react';
import { MobileHeader } from './MobileHeader';
import { Box } from './Box';
import { FilterButton } from './FilterButton';
import { ApplyButton, ClearButton, FilterFooter } from './filters/FilterFooter';
import { Icon } from './Icon2';
import { ModalDialog } from './ModalDialog';
import { Popover } from './Popover';
import { Slider } from './Slider';
import { Stack } from './Stack';

export interface BudgetFilterProps {
  minValue?: number;
  maxValue?: number;
  defaultValue?: number;
  step?: number;
  onChange?: (value: number) => void;
  onClear?: () => void;
  intervals?: number[];
  label?: string;
  alwaysEnabledDoneButton?: boolean;
  overlayPosition?: Omit<AriaPositionProps, 'onClose' | 'overlayRef' | 'targetRef'>;
}

export function BudgetFilter({
  maxValue: maxValueProp = 100,
  minValue: minValueProp,
  onChange,
  onClear,
  intervals,
  label = 'Price',
  overlayPosition,
  defaultValue,
  alwaysEnabledDoneButton = true,
  ...props
}: BudgetFilterProps) {
  let state = useOverlayTriggerState({});

  let triggerRef = React.useRef<any>(null);
  let overlayRef = React.useRef(null);
  const options: NumberFormatOptions = React.useMemo(
    () => ({
      minimumFractionDigits: 0
    }),
    []
  );
  const numberFormatter = useNumberFormatter(options);
  const budgetFormatter: Intl.NumberFormat = React.useMemo(
    () =>
      new Proxy(numberFormatter, {
        get: function (target, prop, receiver) {
          if (prop === 'format') {
            return (value: number) => '$' + target.format(intervals ? intervals[value] : value);
          }
          return Reflect.get(target, prop, receiver);
        }
      }),
    [intervals, numberFormatter]
  );
  const maxValue = intervals ? intervals.length - 1 : maxValueProp;
  const minValue = intervals ? 0 : minValueProp;

  let overlayState = useOverlayTriggerState({
    isOpen: state.isOpen
  });

  // Get props for the trigger and overlay. This also handles
  // hiding the overlay when a parent element of the trigger scrolls
  // (which invalidates the popover positioning).
  let { triggerProps } = useOverlayTrigger({ type: 'dialog' }, overlayState, triggerRef);

  // Get popover positioning props relative to the trigger
  let { overlayProps: positionProps } = useOverlayPosition({
    targetRef: triggerRef,
    overlayRef,
    placement: 'bottom start',
    offset: 0,
    isOpen: state.isOpen,
    ...overlayPosition
  });

  // useButton ensures that focus management is handled correctly,
  // across all browsers. Focus is restored to the button once the
  // popover closes.
  let { buttonProps } = useButton(
    {
      onPress: () => {
        if (state.isOpen) {
          state.close();
        } else {
          state.open();
        }
      }
    },
    triggerRef
  );

  function setDefaultValue() {
    let saved = maxValue;
    let df = -1;
    if (defaultValue) {
      if (intervals) {
        df = intervals.indexOf(defaultValue);
      }
    }
    if (df > -1) {
      saved = df;
    }
    return saved;
  }

  const [savedValue, setSavedValue] = React.useState(setDefaultValue());
  const [value, setValue] = React.useState(savedValue);
  const [filterState, setFilterState] = React.useState<'clean' | 'dirty'>('clean');
  const computedLabel = savedValue === maxValue ? label : budgetFormatter.format(savedValue);
  const isMobile = useIsMobileView();

  const handleClose = () => {
    if (triggerRef && triggerRef?.current) {
      triggerRef?.current?.focus();
    }
    state.close();
    setFilterState('clean');
  };

  const handleTransientChange = (newValue: number[]) => {
    setValue(newValue[0]);
    setFilterState('dirty');
  };

  const handleDismiss = () => {
    handleClose();
    setValue(savedValue);
  };

  const handleClear = () => {
    handleTransientChange([maxValue]);
    onClear?.();
  };

  const handleSave = () => {
    handleClose();
    setSavedValue(value);
    onChange?.(intervals ? intervals[value] : value);
  };

  const sliderValue = React.useMemo(() => [value], [value]);

  React.useEffect(() => {
    if (state.isOpen) {
      setValue(savedValue);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.isOpen]);

  const content = (
    <Content
      onSave={handleSave}
      onClear={handleClear}
      isClearDisabled={value === maxValue}
      isApplyDisabled={alwaysEnabledDoneButton ? false : filterState === 'clean'}
    >
      <Slider
        onChange={handleTransientChange}
        value={sliderValue}
        aria-label={label}
        {...props}
        maxValue={maxValue}
        minValue={minValue}
        numberFormatter={budgetFormatter}
      />
    </Content>
  );

  return (
    <React.Fragment>
      <FilterButton {...buttonProps} {...triggerProps} ref={triggerRef}>
        <span>{computedLabel}</span>
        <Icon aria-hidden="true" icon="keyboard_arrow_down" size={20} />
      </FilterButton>
      {state.isOpen && (
        <OverlayContainer>
          <FocusScope restoreFocus autoFocus contain={isMobile}>
            {isMobile ? (
              <ModalDialog isOpen size="stretch" onClose={handleDismiss}>
                <Stack alignY="space-between" sx={{ height: '100%' }}>
                  <MobileHeader onBackButtonPress={handleDismiss} title={label} />
                  {content}
                </Stack>
              </ModalDialog>
            ) : (
              <Popover
                {...positionProps}
                isOpen={state.isOpen}
                popoverRef={overlayRef}
                sx={{ minWidth: '350px' }}
                onClose={handleDismiss}
              >
                {content}
              </Popover>
            )}
          </FocusScope>
        </OverlayContainer>
      )}
    </React.Fragment>
  );
}

interface ContentProps {
  onSave: () => void;
  onClear: () => void;
  isClearDisabled: boolean;
  isApplyDisabled: boolean;
}

function Content({
  onSave,
  onClear,
  isClearDisabled,
  isApplyDisabled,
  children
}: React.PropsWithChildren<ContentProps>) {
  const focusManager = useFocusManager();
  const handleClear = () => {
    onClear();
    focusManager.focusNext();
  };

  return (
    <React.Fragment>
      <Box p={3} pb={6} sx={{ flex: 1 }} variant="layout.budget">
        {children}
      </Box>
      <FilterFooter
        actionsSlot={
          <React.Fragment>
            <ClearButton onPress={handleClear} isDisabled={isClearDisabled} />
            <ApplyButton onPress={onSave} isDisabled={isApplyDisabled} />
          </React.Fragment>
        }
      />
    </React.Fragment>
  );
}
